/* eslint-disable complexity */
import { LoaderSpinner } from 'carbon-react/lib/components/loader-spinner';
import { capitalize } from 'lodash';
import { useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { Sidebar } from '@futrli/ui-components';
import Typography from 'carbon-react/lib/components/typography';

import { AnyError, useErrorHandler } from '@futrli/errors';
import { useIntl } from '@futrli/localisation';
import { ProductSlug } from '@futrli/shared-services';

import { trackEvent } from '../../../core/tracker';
import { useLoadActivateProductData } from '../../../utils/activate-product-dialog-utils';
import { InformativeDialogContent } from './informative-dialog-content';
import { NonWorkspaceOwnerDialogContent } from './non-workspace-owner-dialog-content';
import { SubscribeAndPurchaseDialogContent } from './subscribe-and-purchase-dialog-content';
import styles from './activate-product-dialog.scss';
import { RenderInPortal } from '../render-in-portal/render-in-portal';
import { PaymentCompleteDialog } from '../../../payment-details/components/payment-complete-dialog';

export type ActivationEvent =
  | React.MouseEvent<HTMLElement>
  | React.KeyboardEvent<HTMLElement>
  | MouseEvent
  | KeyboardEvent;

interface Props {
  workspaceId?: string;
  organisationId?: string;
  className?: string;
  wrapperComponent?: string;
  children?: React.ReactNode;
  isOpenDialog?: boolean;
  defaultMessage?: string;
  productSlug: ProductSlug;
  onSuccess?: () => void;
  onCancel?: () => void;
  onClickCTA?: (event: React.MouseEvent<HTMLElement>) => void;
}
interface ActivateProductState {
  isContentLoading: boolean;
  isOpen: boolean;
  isCompleted: boolean;
  error: AnyError | null;
}

export const ActivateProductSidebar: React.FC<Props> = (props: Props): JSX.Element => {
  const {
    children,
    className,
    organisationId,
    productSlug,
    workspaceId,
    wrapperComponent = 'div',
    onSuccess,
    onCancel,
    onClickCTA
  } = props;
  const intl = useIntl();
  const onManageError = useErrorHandler();
  const params = useParams();
  const workspId = workspaceId || params.workspaceId || '';
  const orgId = organisationId || params.organisationId;
  const [subscribeDialogTitle, setSubscribeDialogTitle] = useState<string>(props.defaultMessage || '');
  const [activateProductState, setActivateProductState] = React.useState<ActivateProductState>({
    isOpen: false,
    isContentLoading: true,
    isCompleted: false,
    error: null
  });

  useEffect(() => {
    setActivateProductState(prevState => ({
      ...prevState,
      isOpen: !!props.isOpenDialog
    }));
  }, [props.isOpenDialog]);

  const { isOpen, isCompleted, isContentLoading } = activateProductState;
  const { activateProductData, isActivateProductDataLoading, resetActivateProductData } = useLoadActivateProductData(
    workspId,
    productSlug,
    isOpen,
    orgId
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onClickCTA?.(event);
    setActivateProductState(prevState => ({
      ...prevState,
      isOpen: true
    }));
  };

  const handleChangeSubscribeDialogTitle = useCallback((newTitle: string) => {
    setSubscribeDialogTitle(newTitle);
  }, []);

  const handleClose = useCallback(
    (event: ActivationEvent) => {
      event.stopPropagation();
      setActivateProductState({
        isOpen: false,
        isContentLoading: true,
        isCompleted: false,
        error: null
      });
      resetActivateProductData();
      onCancel?.();
      setSubscribeDialogTitle(props.defaultMessage || '');
    },
    [onCancel, resetActivateProductData, props.defaultMessage]
  );

  const handleLetsGoAction = (event: ActivationEvent) => {
    onSuccess?.();
    handleClose(event);
  };

  const handleLoading = useCallback((isContentLoading: boolean) => {
    setActivateProductState(prevState => ({
      ...prevState,
      isContentLoading
    }));
  }, []);

  const handleComplete = useCallback(() => {
    trackEvent(`${capitalize(productSlug)} upgraded to Pro`);
    setActivateProductState(prevState => ({
      ...prevState,
      isContentLoading: false,
      isCompleted: true
    }));
  }, [productSlug]);

  const handleError = useCallback((error: AnyError) => {
    setActivateProductState(prevState => ({
      ...prevState,
      error,
      isContentLoading: false
    }));
    onManageError(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isCompleted) {
    return (
      <React.Fragment>
        {React.createElement(wrapperComponent, { className, onClick: handleOpen }, children)}
        <RenderInPortal>
          <PaymentCompleteDialog
            titleMessage={`${intl.formatMessage({
              id: 'billing.success',
              defaultMessage: 'Success!'
            })}\n${intl.formatMessage({
              id: 'billing.thank_you_for_your_subscription',
              defaultMessage: 'Thank you for your subscription'
            })}`}
            onSubmit={handleLetsGoAction}
          />
        </RenderInPortal>
      </React.Fragment>
    );
  }

  if (isActivateProductDataLoading || !activateProductData) {
    return (
      <React.Fragment>
        {React.createElement(wrapperComponent, { className, onClick: handleOpen }, children)}
        <Sidebar
          open={isOpen}
          onCancel={handleClose}
          aria-label={intl.formatMessage({
            id: 'billing.activate_product_dialog_loading.aria_label',
            defaultMessage: 'Activate product dialog loading'
          })}
          size="medium-large"
        >
          <div className={styles.loader}>
            <LoaderSpinner showSpinnerLabel={false} />
          </div>
        </Sidebar>
      </React.Fragment>
    );
  }

  // Error dialog
  if (activateProductState.error) {
    return (
      <React.Fragment>
        {React.createElement(wrapperComponent, { className, onClick: handleOpen }, children)}
        <Sidebar
          open={isOpen}
          onCancel={handleClose}
          aria-label={intl.formatMessage({
            id: 'billing.activate_product_dialog_error.aria_label',
            defaultMessage: 'Activate product dialog error'
          })}
          header={
            <Typography variant="h2" fontWeight="700">
              {intl.formatMessage({ id: 'billing.oops', defaultMessage: 'Oops!' })}
            </Typography>
          }
          size="medium-large"
        >
          {isContentLoading && (
            <div className={styles.loader}>
              <LoaderSpinner showSpinnerLabel={false} />
            </div>
          )}
          <InformativeDialogContent onLoading={handleLoading} onClose={handleClose} />
        </Sidebar>
      </React.Fragment>
    );
  }

  // Non workspace owner dialog
  if (!activateProductData.user.isWorkspaceOwner && activateProductData.organisation) {
    return (
      <React.Fragment>
        {React.createElement(wrapperComponent, { className, onClick: handleOpen }, children)}
        <Sidebar
          open={isOpen}
          onCancel={handleClose}
          aria-label={intl.formatMessage({
            id: 'billing.upgrade_to_pro',
            defaultMessage: 'Upgrade to pro!'
          })}
          header={
            <Typography variant="h2" fontWeight="700">
              {intl.formatMessage({ id: 'billing.upgrade_to_pro', defaultMessage: 'Upgrade to pro!' })}
            </Typography>
          }
          size="medium-large"
        >
          {isContentLoading && (
            <div className={styles.loader}>
              <LoaderSpinner showSpinnerLabel={false} />
            </div>
          )}
          <NonWorkspaceOwnerDialogContent
            workspaceId={workspId}
            organisationId={activateProductData.organisation.id}
            product={activateProductData.product}
            onLoading={handleLoading}
            onClose={handleClose}
            onError={handleError}
          />
        </Sidebar>
      </React.Fragment>
    );
  }

  // Subscribe and purchase dialog
  if (!activateProductData?.billing.subscription) {
    return (
      <React.Fragment>
        {React.createElement(wrapperComponent, { className, onClick: handleOpen }, children)}
        <Sidebar
          open={isOpen}
          onCancel={handleClose}
          aria-label={intl.formatMessage({
            id: 'billing.activate_product_dialog.aria_label',
            defaultMessage: 'Activate product dialog'
          })}
          header={
            subscribeDialogTitle ? (
              <Typography variant="h2" fontWeight="700">
                {subscribeDialogTitle}
              </Typography>
            ) : (
              <div style={{ height: 28 }} />
            )
          }
          size="medium-large"
        >
          {isContentLoading && (
            <div className={styles.loader}>
              <LoaderSpinner showSpinnerLabel={false} />
            </div>
          )}
          <SubscribeAndPurchaseDialogContent
            isLoading={isContentLoading}
            workspaceId={workspId}
            organisation={activateProductData.organisation}
            product={activateProductData.product}
            onLoading={handleLoading}
            onComplete={handleComplete}
            onClose={handleClose}
            onError={handleError}
            defaultMessage={props.defaultMessage}
            isOpen={isOpen}
            handleChangeSubscribeDialogTitle={handleChangeSubscribeDialogTitle}
          />
        </Sidebar>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {React.createElement(wrapperComponent, { className, onClick: handleOpen }, children)}
      <Sidebar
        open={isOpen}
        onCancel={handleClose}
        aria-label={intl.formatMessage({
          id: 'billing.activate_product_dialog.aria_label',
          defaultMessage: 'Activate product dialog'
        })}
        size="medium-large"
      >
        <div />
      </Sidebar>
    </React.Fragment>
  );
};
